import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  PaEmptyState,
  PaPageHeader,
  PaSkeleton,
} from "components/platform-admin";
import PlatformAdminLayout from "pages/PlatformAdmin/layout/PlatformAdminLayout";
import { getTimeOfDayGreeting } from "pages/PlatformAdmin/platformAdminPage.utils";
import { getUser } from "store/selector/auth.selector";
import {
  fetchPlatformBillingOverview,
  fetchPlatformDashboardStats,
} from "store/services/platformAdmin.services";
import { FINANCIAL_OVERVIEW_SUMMARY_SECTION } from "../Billing/billingOverview.utils";
import PlatformDashboardQuickActions from "./PlatformDashboardQuickActions";
import PlatformDashboardSummary from "./PlatformDashboardSummary";
import {
  PlatformDashboardCatalogSummary,
  PlatformDashboardAiUsageSummary,
  PlatformDashboardOperationsPanel,
  PlatformDashboardUsageSummary,
  formatUsageRefreshedLabel,
} from "./PlatformDashboardRolePanels";
import { usePlatformDashboardCapabilities } from "pages/PlatformAdmin/hooks/usePlatformPermissions";
import {
  buildSuspendedAttentionItems,
  getDashboardPanelVisibility,
  getDashboardProfileMeta,
  getDashboardWorkspaceSubtitle,
  getPlatformRoleLabel,
  resolveDashboardProfile,
  shouldFetchBillingOverview,
  shouldFetchDashboardStats,
  visibleSectionsFromCapabilities,
} from "./platformDashboardCapabilities.utils";
import {
  buildNeedsAttentionItems,
  buildRecentSignupItems,
  formatRecentActivityPrimary,
  formatRecentActivitySecondary,
  getGreetingName,
} from "./platformDashboard.utils";

const PlatformDashboard = () => {
  const currentUser = useSelector(getUser);
  const capabilities = usePlatformDashboardCapabilities();

  const [statsLoading, setStatsLoading] = useState(() =>
    shouldFetchDashboardStats(capabilities)
  );
  const [billingLoading, setBillingLoading] = useState(() =>
    shouldFetchBillingOverview(capabilities)
  );
  const [statsPayload, setStatsPayload] = useState(null);
  const [billingPayload, setBillingPayload] = useState(null);
  const [error, setError] = useState(null);

  const fallbackSections = useMemo(
    () => visibleSectionsFromCapabilities(capabilities),
    [capabilities]
  );
  const visibleSections = statsPayload?.visible_sections ?? fallbackSections;
  const primaryRoles = statsPayload?.primary_roles ?? [];
  const profile = useMemo(
    () => resolveDashboardProfile(capabilities, primaryRoles),
    [capabilities, primaryRoles]
  );
  const profileMeta = useMemo(() => getDashboardProfileMeta(profile), [profile]);
  const panels = useMemo(
    () => getDashboardPanelVisibility(capabilities, visibleSections),
    [capabilities, visibleSections]
  );

  useEffect(() => {
    if (!shouldFetchDashboardStats(capabilities)) {
      setStatsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadDashboard = async () => {
      setStatsLoading(true);
      setError(null);

      try {
        const stats = await fetchPlatformDashboardStats();
        if (cancelled) {
          return;
        }

        setStatsPayload(stats);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load dashboard");
        }
      } finally {
        if (!cancelled) {
          setStatsLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [capabilities]);

  useEffect(() => {
    if (!shouldFetchBillingOverview(capabilities)) {
      return undefined;
    }

    let cancelled = false;

    const loadBilling = async () => {
      setBillingLoading(true);

      try {
        const billing = await fetchPlatformBillingOverview(FINANCIAL_OVERVIEW_SUMMARY_SECTION);
        if (!cancelled) {
          setBillingPayload(billing);
        }
      } catch {
        if (!cancelled) {
          setBillingPayload(null);
        }
      } finally {
        if (!cancelled) {
          setBillingLoading(false);
        }
      }
    };

    loadBilling();

    return () => {
      cancelled = true;
    };
  }, [capabilities]);

  const needsAttention = useMemo(
    () =>
      buildNeedsAttentionItems(
        panels.showNeedsAttention ? statsPayload?.trials_expiring : [],
        panels.showNeedsAttention ? statsPayload?.failed_payments : []
      ),
    [panels.showNeedsAttention, statsPayload]
  );

  const operationsAttention = useMemo(
    () =>
      panels.showOperationsAttention
        ? buildSuspendedAttentionItems(statsPayload?.suspended_tenants)
        : [],
    [panels.showOperationsAttention, statsPayload]
  );

  const recentActivity = panels.showRecentActivity
    ? statsPayload?.recent_activity ?? []
    : [];
  const recentSignups = useMemo(
    () =>
      panels.showRecentSignups
        ? buildRecentSignupItems(statsPayload?.recent_signups)
        : [],
    [panels.showRecentSignups, statsPayload]
  );

  const roleLabel = getPlatformRoleLabel(currentUser, primaryRoles);
  const computedLabel = statsPayload?.computed_at
    ? moment(statsPayload.computed_at).fromNow()
    : null;
  const subtitle = getDashboardWorkspaceSubtitle(profileMeta, computedLabel);

  const attentionItems = panels.showNeedsAttention ? needsAttention : operationsAttention;
  const showAttentionPanel = panels.showNeedsAttention || panels.showOperationsAttention;
  const attentionTitle = panels.showNeedsAttention ? "Needs attention" : "Suspended tenants";
  const attentionEmptyTitle = panels.showNeedsAttention ? "All clear" : "No suspended tenants";
  const attentionEmptyMessage = panels.showNeedsAttention
    ? "No tenants need attention right now"
    : "Suspended service providers appear here";

  const visiblePanelCount = [
    showAttentionPanel,
    panels.showRecentSignups,
    panels.showRecentActivity,
  ].filter(Boolean).length;

  return (
    <PlatformAdminLayout>
      <PaPageHeader
        title={`${getTimeOfDayGreeting()}, ${getGreetingName(currentUser)}`}
        subtitle={`${roleLabel} · ${profileMeta.title} · ${subtitle}`}
        className="mb-4"
      />

      {error ? (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
            billingPayload
              ? "border-amber-200 bg-amber-50 text-amber-800"
              : "border-red-200 bg-red-50 text-red-700"
          }`}
        >
          {billingPayload
            ? "Some dashboard widgets failed to load. Figures from completed requests are still shown."
            : error}
        </div>
      ) : null}

      {panels.showQuickActions ? (
        <PlatformDashboardQuickActions capabilities={capabilities} profile={profile} />
      ) : null}

      <PlatformDashboardSummary
        stats={statsPayload?.stats}
        billingPayload={billingPayload}
        capabilities={capabilities}
        visibleSections={visibleSections}
        loading={statsLoading}
        billingLoading={billingLoading}
      />

      {panels.showCatalogSummary ? (
        <PlatformDashboardCatalogSummary catalog={statsPayload?.catalog} loading={statsLoading} />
      ) : null}

      {panels.showUsageSummary ? (
        <PlatformDashboardUsageSummary usage={statsPayload?.usage} loading={statsLoading} />
      ) : null}

      {panels.showAiUsageSummary ? (
        <PlatformDashboardAiUsageSummary aiUsage={statsPayload?.ai_usage} loading={statsLoading} />
      ) : null}

      {formatUsageRefreshedLabel(statsPayload?.usage?.last_refreshed_at) ? (
        <p className="mb-4 text-xs text-gray-500">
          {formatUsageRefreshedLabel(statsPayload?.usage?.last_refreshed_at)}
        </p>
      ) : null}

      {visiblePanelCount > 0 ? (
        <div
          className={`mt-4 grid grid-cols-1 gap-4 ${
            visiblePanelCount >= 3 ? "lg:grid-cols-3" : visiblePanelCount === 2 ? "lg:grid-cols-2" : ""
          }`}
        >
          {showAttentionPanel ? (
            panels.showOperationsAttention && !panels.showNeedsAttention ? (
              <PlatformDashboardOperationsPanel items={operationsAttention} loading={statsLoading} />
            ) : (
              <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h2 className="text-sm font-semibold text-gray-900">{attentionTitle}</h2>
                  {capabilities.canViewBilling ? (
                    <Link
                      to="/billing?tab=follow-up&focus=failed_payments"
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      Billing queue →
                    </Link>
                  ) : null}
                </div>

                {statsLoading ? (
                  <PaSkeleton variant="text" lines={4} />
                ) : attentionItems.length === 0 ? (
                  <PaEmptyState
                    title={attentionEmptyTitle}
                    message={attentionEmptyMessage}
                    description={
                      panels.showNeedsAttention
                        ? "Trial endings and billing issues appear here."
                        : undefined
                    }
                  />
                ) : (
                  <ul className="space-y-2">
                    {attentionItems.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={
                            item.tenantId
                              ? `/tenants/${item.tenantId}?tab=billing`
                              : "/billing"
                          }
                          className={`block border-l-4 ${item.borderClass} rounded-r-lg bg-gray-50 px-3 py-2 transition hover:bg-gray-100`}
                        >
                          <p className="text-sm font-medium text-gray-900">{item.label}</p>
                          {item.detail ? (
                            <p className="mt-0.5 text-xs capitalize text-gray-500">{item.detail}</p>
                          ) : null}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )
          ) : null}

          {panels.showRecentSignups ? (
            <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold text-gray-900">Recent signups</h2>
                <Link
                  to="/tenants"
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
                >
                  View tenants →
                </Link>
              </div>

              {statsLoading ? (
                <PaSkeleton variant="text" lines={4} />
              ) : recentSignups.length === 0 ? (
                <PaEmptyState
                  title="No recent signups"
                  message="New tenant registrations will appear here"
                />
              ) : (
                <ul className="divide-y divide-gray-100">
                  {recentSignups.map((tenant) => (
                    <li key={tenant.id} className="flex items-start justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <Link
                          to={`/tenants/${tenant.id}`}
                          className="truncate text-sm font-medium text-indigo-600 hover:text-indigo-700"
                        >
                          {tenant.name}
                        </Link>
                        {tenant.email ? (
                          <p className="truncate text-xs text-gray-500">{tenant.email}</p>
                        ) : null}
                      </div>
                      <time
                        className="shrink-0 text-xs text-gray-500"
                        dateTime={tenant.createdAt}
                        title={
                          tenant.createdAt
                            ? moment(tenant.createdAt).format("D MMM YYYY, h:mm a")
                            : undefined
                        }
                      >
                        {tenant.createdAt ? moment(tenant.createdAt).fromNow() : "—"}
                      </time>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}

          {panels.showRecentActivity ? (
            <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold text-gray-900">Recent platform activity</h2>
                <Link
                  to="/audit-log"
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
                >
                  Audit log →
                </Link>
              </div>

              {statsLoading ? (
                <PaSkeleton variant="text" lines={4} />
              ) : recentActivity.length === 0 ? (
                <PaEmptyState
                  title="No recent activity"
                  message="Platform audit events will appear here"
                  description="Open the audit log for a full history."
                />
              ) : (
                <ul className="divide-y divide-gray-100">
                  {recentActivity.map((activity) => (
                    <li key={activity.id} className="flex items-start justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {formatRecentActivityPrimary(activity)}
                        </p>
                        <p className="truncate text-xs text-gray-500">
                          {formatRecentActivitySecondary(activity)}
                        </p>
                      </div>
                      <time
                        className="shrink-0 text-xs text-gray-500"
                        dateTime={activity.created_at}
                        title={
                          activity.created_at
                            ? moment(activity.created_at).format("D MMM YYYY, h:mm a")
                            : undefined
                        }
                      >
                        {activity.created_at
                          ? moment(activity.created_at).fromNow()
                          : "—"}
                      </time>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}
        </div>
      ) : null}
    </PlatformAdminLayout>
  );
};

export default PlatformDashboard;
